// src/utils/tallerEstadoHelper.ts

const BUFFER_MINUTOS = 30;

/**
 * Parsea una fecha y hora en objetos Date exactos.
 * Retorna { inicio, termino } con las horas configuradas.
 */
const parsearFechasEvento = (fecha: Date, horaInicio: string, horaTermino: string) => {
  const fechaTaller = new Date(fecha);
  const [horaInicioH, horaInicioM] = horaInicio.split(':').map(Number);
  const [horaTerminoH, horaTerminoM] = horaTermino.split(':').map(Number);

  const inicio = new Date(fechaTaller);
  inicio.setHours(horaInicioH, horaInicioM, 0, 0);

  const termino = new Date(fechaTaller);
  termino.setHours(horaTerminoH, horaTerminoM, 0, 0);

  return { inicio, termino };
};

/**
 * Calcula el estado de un taller basado en su fecha y hora.
 *
 * - "proximo": antes de que inicie el evento
 * - "en_curso": desde la hora de inicio hasta la hora de término
 * - "finalizado": después de la hora de término
 * - "cancelado": solo si se establece manualmente
 */
export const calcularEstadoTaller = (
  fecha: Date,
  horaInicio: string,
  horaTermino: string,
  estadoActual?: string
): 'proximo' | 'en_curso' | 'finalizado' | 'cancelado' => {
  if (estadoActual === 'cancelado') {
    return 'cancelado';
  }

  const ahora = new Date();
  const { inicio, termino } = parsearFechasEvento(fecha, horaInicio, horaTermino);

  if (ahora > termino) {
    return 'finalizado';
  }

  if (ahora >= inicio && ahora <= termino) {
    return 'en_curso';
  }

  return 'proximo';
};

/**
 * Determina si un usuario aún puede inscribirse a un taller.
 * Permite inscripción hasta 30 minutos antes de la hora de inicio.
 *
 * Ejemplo: si el curso inicia a las 4:30 PM, a las 4:00 PM aún puede inscribirse.
 */
export const puedeRegistrarse = (
  fecha: Date,
  horaInicio: string,
  horaTermino: string,
  estadoActual?: string
): boolean => {
  if (estadoActual === 'cancelado') return false;

  const ahora = new Date();
  const { inicio, termino } = parsearFechasEvento(fecha, horaInicio, horaTermino);

  // Límite: 30 minutos antes del inicio
  const limiteInscripcion = new Date(inicio.getTime() - BUFFER_MINUTOS * 60 * 1000);

  // Puede registrarse si aún no pasó el límite y el evento no terminó
  return ahora <= limiteInscripcion && ahora <= termino;
};

/**
 * Obtiene el badge HTML para el estado del taller
 * @param estado - Estado del taller
 * @returns Objeto con clase CSS y texto para el badge
 */
export const obtenerBadgeEstado = (estado?: string) => {
  switch (estado) {
    case 'proximo':
      return { className: 'bg-primary', texto: '📅 Próximo' };
    case 'en_curso':
      return { className: 'bg-success', texto: '▶️ En Curso' };
    case 'finalizado':
      return { className: 'bg-secondary', texto: '✅ Finalizado' };
    case 'cancelado':
      return { className: 'bg-danger', texto: '❌ Cancelado' };
    default:
      return { className: 'bg-info', texto: '📌 Sin Estado' };
  }
};

/**
 * Verifica si un taller necesita actualización de estado
 * @param estadoActual - Estado actual en Firestore
 * @param fecha - Fecha del evento
 * @param horaInicio - Hora de inicio
 * @param horaTermino - Hora de término
 * @returns true si necesita actualización, false si no
 */
export const necesitaActualizacionEstado = (
  estadoActual: string | undefined,
  fecha: Date,
  horaInicio: string,
  horaTermino: string
): boolean => {
  if (!estadoActual || estadoActual === 'cancelado') {
    return false;
  }

  const estadoCalculado = calcularEstadoTaller(fecha, horaInicio, horaTermino, estadoActual);
  return estadoActual !== estadoCalculado;
};

/**
 * Determina si un taller está activo (próximo o en curso) para mostrarlo destacado.
 */
export const esTallerActivo = (estado?: string): boolean => {
  return estado === 'proximo' || estado === 'en_curso';
};
